import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError, formatCHF, formatDateTime } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Bank, WarningCircle, CheckCircle, Copy, DownloadSimple, Receipt } from "@phosphor-icons/react";

/**
 * Admin view: pending payouts grouped by coach. Admin marks a group as paid
 * once the SEPA/bank transfer has been executed, providing a reference.
 */
export default function AdminPayouts() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [reference, setReference] = useState({});
  const [marking, setMarking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payouts");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const copyIban = (iban) => {
    if (!iban) return;
    navigator.clipboard?.writeText(iban);
    toast.success(t("admin_dash.iban_copied"));
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/payouts.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "payouts.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markPaid = async (row) => {
    const ref = (reference[row.instructor.id] || "").trim();
    if (!window.confirm(t("admin_dash.confirm_mark_paid", { name: row.instructor.name, amount: formatCHF(row.total_chf) }))) return;
    setMarking(row.instructor.id);
    try {
      await api.post("/admin/payouts/mark-paid", { instructor_id: row.instructor.id, reference: ref || null });
      toast.success(t("admin_dash.marked_paid"));
      setRows((prev) => prev.filter((r) => r.instructor.id !== row.instructor.id));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMarking(null);
    }
  };

  const totalPending = rows.reduce((s, r) => s + r.total_chf, 0);

  return (
    <div className="space-y-4" data-testid="admin-payouts">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("admin_dash.payouts_total_pending")}</div>
          <div className="font-display text-3xl font-black text-[#FFB800]" data-testid="payouts-total">{formatCHF(totalPending)}</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadCsv}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5 uppercase tracking-widest text-[10px] font-bold rounded-none h-10"
            data-testid="payouts-download-csv">
            <DownloadSimple size={14} className="mr-2" weight="bold" />{t("admin_dash.download_csv")}
          </Button>
          <Button onClick={load} className="bg-white/5 hover:bg-white/10 text-white uppercase tracking-widest text-[10px] font-bold rounded-none h-10" data-testid="payouts-refresh">
            {t("admin_dash.users_count", { count: rows.length })}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] text-zinc-400 text-xs uppercase tracking-widest">{t("common.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] text-zinc-400" data-testid="payouts-empty">
          <Receipt size={40} weight="thin" className="mx-auto text-zinc-600" />
          <div className="mt-3 text-xs uppercase tracking-widest">{t("admin_dash.payouts_empty")}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const missingIban = !row.instructor.payout_ready;
            const open = !!expanded[row.instructor.id];
            return (
              <div key={row.instructor.id} className={`border ${missingIban ? "border-[#E30613]/40" : "border-white/10"} bg-[#0A0A0A]`} data-testid={`payout-row-${row.instructor.id}`}>
                <div className="p-4 flex flex-wrap items-center gap-4 justify-between">
                  <div className="min-w-[220px] flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display uppercase font-black text-lg text-white">{row.instructor.name}</span>
                      {missingIban ? (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#E30613] font-bold border border-[#E30613]/40 px-2 py-0.5">
                          <WarningCircle size={10} weight="fill" />{t("admin_dash.iban_missing")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#00FF66] font-bold border border-[#00FF66]/40 px-2 py-0.5">
                          <CheckCircle size={10} weight="fill" />{t("profile.payout_ready")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">{row.instructor.email}</div>
                    {row.instructor.iban && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-300">{row.instructor.iban}</span>
                        <button onClick={() => copyIban(row.instructor.iban)} className="text-zinc-500 hover:text-white" title={t("admin_dash.copy_iban")} data-testid={`copy-iban-${row.instructor.id}`}>
                          <Copy size={14} weight="bold" />
                        </button>
                      </div>
                    )}
                    {row.instructor.iban_holder_name && (
                      <div className="text-[11px] text-zinc-500 mt-1">
                        <Bank size={11} className="inline mr-1" weight="bold" />
                        {row.instructor.iban_holder_name}
                        {row.instructor.iban_bank_name && <span> · {row.instructor.iban_bank_name}</span>}
                      </div>
                    )}
                  </div>

                  <div className="text-right">
                    <div className="font-display text-2xl font-black text-[#FFB800]">{formatCHF(row.total_chf)}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                      {row.bookings_count} {t("admin_dash.bookings")}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 min-w-[220px]">
                    <Input value={reference[row.instructor.id] || ""}
                      onChange={(e) => setReference({ ...reference, [row.instructor.id]: e.target.value })}
                      placeholder={t("admin_dash.payout_reference_placeholder")}
                      className="bg-black border-white/20 rounded-none h-9 text-xs"
                      data-testid={`payout-ref-${row.instructor.id}`} />
                    <Button
                      disabled={missingIban || marking === row.instructor.id}
                      onClick={() => markPaid(row)}
                      className="bg-[#00FF66] hover:bg-[#00E060] text-black uppercase tracking-widest text-[10px] font-bold rounded-none h-9 disabled:opacity-40"
                      data-testid={`payout-mark-paid-${row.instructor.id}`}>
                      {marking === row.instructor.id ? "..." : t("admin_dash.mark_paid")}
                    </Button>
                  </div>
                </div>

                <button
                  onClick={() => setExpanded({ ...expanded, [row.instructor.id]: !open })}
                  className="w-full px-4 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-zinc-500 border-t border-white/5 hover:text-white"
                  data-testid={`payout-expand-${row.instructor.id}`}>
                  {open ? "▲" : "▼"} {t("admin_dash.show_bookings")}
                </button>
                {open && (
                  <div className="px-4 pb-4 space-y-1 border-t border-white/5">
                    {row.bookings.map((b) => (
                      <div key={b.id} className="text-xs text-zinc-400 flex items-center justify-between py-1">
                        <span>{b.course_title} <span className="text-zinc-600">· {formatDateTime(b.starts_at)}</span></span>
                        <span className="font-mono text-[#FFB800]">{formatCHF(b.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
